import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, Wine, FileText, Globe, Truck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#0066FF] hover:underline">
    {children}
  </a>
);

const chain = [
  { icon: Globe, title: "Proveedor UE o terceros países", text: "Holanda, Bélgica, Francia, Escocia o importación extracomunitaria con DUA." },
  { icon: Truck, title: "Transporte EMCS", text: "Bajo régimen suspensivo con documento e-AD (sistema EMCS)." },
  { icon: Wine, title: "Recepción en depósito fiscal", text: "BCN Bonded Barcelona, Madrid u otros depósitos colaboradores." },
  { icon: FileText, title: "Extracción y puesta a consumo", text: "FiscalBond gestiona la extracción cuando el cliente lo necesita o reexpedición a la UE." },
];

const targets = [
  "Importadores de vino y espirituosos desde la UE o terceros países",
  "Embotelladores que reciben alcohol a granel y envasan en España",
  "Distribuidores mayoristas de bebidas alcohólicas",
  "Plataformas de e-commerce de vinos y espirituosos con stock en depósito",
  "Empresas de hostelería con volúmenes de compra elevados",
];

export default function SectorAlcohol() {
  return (
    <div className="App">
      <Navbar />
      <main className="bg-[#0A1628]">
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0E1F38] to-[#0A1628]" />
          <div className="absolute -top-20 left-1/3 w-[500px] h-[500px] rounded-full bg-[#0066FF]/15 blur-[140px]" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="back-to-home">
              <ChevronLeft className="w-4 h-4" /> Volver al inicio
            </Link>
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B00] font-semibold">Sector</span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
              style={{ fontFamily: "Poppins" }}
            >
              Alcohol y Bebidas Derivadas
            </motion.h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-3xl">
              El impuesto especial sobre el alcohol es uno de los más elevados de España. Una importación de alcohol etílico a 96°vol. tributa a <strong className="text-white">830,25 €/hl</strong> de alcohol puro. Importar un camión cisterna de 24.000 litros de alcohol a 96°vol. genera un impuesto especial de aproximadamente <strong className="text-[#FF6B00]">191.100€</strong>.
            </p>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed max-w-3xl">
              Operar bajo régimen suspensivo con FiscalBond significa diferir ese pago hasta la venta efectiva del producto, liberando más de <strong className="text-[#0066FF]">190.000€</strong> de tesorería por operación.
            </p>
          </div>
        </section>

        <section className="bg-[#F5F7FA] text-[#1A1A2E] py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily: "Poppins" }}>
              Cadena típica de operación con FiscalBond
            </h2>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {chain.map(({ icon: Icon, title, text }, i) => (
                <div key={title} className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0066FF] hover:-translate-y-1 transition-all">
                  <div className="absolute top-4 right-4 text-xs font-bold text-slate-300 tabular-nums">0{i + 1}</div>
                  <div className="w-12 h-12 rounded-xl bg-[#0066FF]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#0066FF]" />
                  </div>
                  <h3 className="font-semibold text-[#0A1628]" style={{ fontFamily: "Poppins" }}>{title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-20 text-3xl md:text-4xl font-bold" style={{ fontFamily: "Poppins" }}>
              Empresas que trabajan con nosotros
            </h2>
            <ul className="mt-8 grid md:grid-cols-2 gap-3 max-w-4xl">
              {targets.map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0066FF] flex-shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-[#0A1628] py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "Poppins" }}>
              Normativa clave
            </h2>
            <ul className="mt-8 space-y-3 text-slate-300">
              <li>→ <Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Ley 38/1992, Título II — Impuesto sobre el Alcohol</Ext></li>
              <li>→ <Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32019R0787">Reglamento UE 2019/787 sobre bebidas espirituosas</Ext></li>
              <li>→ <Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A31992L0083">Directiva 92/83/CEE (estructura del impuesto sobre el alcohol)</Ext></li>
            </ul>

            <a
              href="/#contacto"
              data-testid="sector-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium px-7 py-4 rounded-xl transition-all shadow-xl shadow-[#0066FF]/30 hover:-translate-y-0.5"
            >
              Solicitar información
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
